import React, { useMemo } from 'react';
import moment from 'moment';
import { InternalFormSubmissionHistory } from '@dill/dill-shared';
import { DillModal } from '../../../general/Dill/DillModal';
import { DillButton } from '../../../general/Dill/DillButton';
import { DillColumn, DillTable } from '../../../general/Dill/DillTable';
import { InternalFormStatusBadge } from './InternalFormSubmissions';

const STATUS_LABELS: Record<InternalFormSubmissionHistory['status'], string> = {
  SENT: 'Sent',
  AWAITING_SIGNATURES: 'Awaiting Signatures',
  SUBMITTED: 'Ready for Decision',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
};

// Same table as a credit application's Application History (CreditAppHistoryModal).
const InternalFormHistoryModal = ({
  open,
  handleClose,
  history,
  formName,
}: {
  open: boolean;
  handleClose: () => void;
  history: InternalFormSubmissionHistory[];
  formName: string;
}) => {
  const columns = useMemo<DillColumn<InternalFormSubmissionHistory>[]>(
    () => [
      {
        key: 'status',
        label: 'Status',
        width: 160,
        sortable: false,
        render: (_, row) => <InternalFormStatusBadge status={row.status} />,
        getSearchValue: (row) => STATUS_LABELS[row.status] ?? row.status,
      },
      {
        key: 'date',
        label: 'Date',
        width: 150,
        sortable: false,
        render: (_, row) => (
          <p className="w-full text-xs">{moment(row.date).format('MM/DD/YYYY LT')}</p>
        ),
        getSearchValue: (row) => moment(row.date).format('MM/DD/YYYY LT'),
      },
      {
        key: 'form',
        label: 'Form',
        width: 180,
        sortable: false,
        render: () => <p className="w-full text-xs">{formName}</p>,
        getSearchValue: () => formName,
      },
      {
        key: 'actionBy',
        label: 'Action By',
        width: 180,
        sortable: false,
        render: (_, row) => <p className="w-full text-xs break-all">{row.actionBy || '-'}</p>,
        getSearchValue: (row) => row.actionBy ?? '',
      },
      {
        key: 'notes',
        label: 'Notes',
        width: 320,
        sortable: false,
        render: (_, row) => <p className="w-full text-xs whitespace-pre-wrap">{row.notes}</p>,
        getSearchValue: (row) => row.notes ?? '',
      },
    ],
    [formName]
  );

  return (
    <DillModal
      open={open}
      onClose={handleClose}
      title="Form History"
      maxWidth="80vw"
      footer={
        <div className="flex justify-end">
          <DillButton label="Close" variant="secondary" onClick={handleClose} />
        </div>
      }>
      <DillTable
        columns={columns}
        data={history}
        search
        searchPlaceholder="Search..."
        pagination={false}
        emptyMessage="No history available."
        striped={false}
        rowHeight={74}
        resizable
        maxHeight="calc(100vh - 320px)"
        showCount={false}
      />
    </DillModal>
  );
};

export default InternalFormHistoryModal;
